import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import type { BrandMode, BrandThemeConfig } from './theme.interface';
import { applyCssVariables } from './theme.vars';
import { mapToAntdTheme } from './theme.mapper';
import { resolveTheme, defaultTheme } from './theme.tokens';
import { ThemeContext } from './theme.context';

type Props = {
	brandTheme?: BrandThemeConfig;
	children: React.ReactNode;
};

const readStoredMode = (): BrandMode | undefined => {
    try {
        const raw = window.localStorage.getItem('ui.theme.mode');
        if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
    } catch { /* ignore */ }
    return undefined;
};

const ThemeProvider: React.FC<Props> = ({ brandTheme, children }) => {
    const [brand, setBrand] = useState<BrandThemeConfig | undefined>(() => {
        const stored = readStoredMode();
        const base = brandTheme ?? defaultTheme;
        return stored ? { ...base, mode: stored } : brandTheme;
    });

    const resolved = useMemo(() => resolveTheme(brand), [brand]);
    const antd = useMemo(() => mapToAntdTheme(resolved), [resolved]);

    useEffect(() => {
        try {
            const currentMode = brand?.mode ?? 'light';
            window.localStorage.setItem('ui.theme.mode', currentMode);
        } catch {
            console.error('Error saving theme mode to localStorage');
        }
    }, [brand]);

    useEffect(() => {
        applyCssVariables(resolved);
    }, [resolved]);

    const value = useMemo(
        () => ({
            theme: resolved,
            setBrandTheme: setBrand,
            setMode: (mode: BrandMode) => setBrand((prev) => ({ ...(prev ?? defaultTheme), mode })),
        }),
        [resolved],
    );

    return (
        <ThemeContext.Provider value={value}>
            <ConfigProvider theme={antd} form={{ requiredMark: false }}>{children}</ConfigProvider>
        </ThemeContext.Provider>
    );
};

export { ThemeProvider };
